import { Link } from "@tanstack/react-router";
import { Banknote, Coins, LifeBuoy, ListChecks, Lock, ShieldCheck, Timer } from "lucide-react";
import { ContactForm } from "@/components/contact-form";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { EARN_FEE_PERCENT } from "@/lib/wallet-constants";
import { MP_EARNINGS_PENDING_DAYS, MP_SAFETY_HOLD_DAYS } from "@/lib/marketplace/content";
import { useT } from "@/i18n";

const FAQ_EN: [string, string][] = [
  ["What does it cost to earn on BeHot.Now?", `Nothing upfront. Publishing services and keeping them live is free; the platform takes ${EARN_FEE_PERCENT}% only from completed earnings.`],
  ["When can I withdraw?", `Earnings clear into the Earn wallet ${MP_EARNINGS_PENDING_DAYS} days after the order completes. From US$100 you can request a withdrawal at any time.`],
  ["How long does a payout take?", "We verify the request within 48 hours. Once approved, bank transfers and crypto payouts can take 5 to 8 working days depending on your bank or network."],
  ["Which payout methods are supported?", "Bank transfer in EUR, USD or AED with IBAN and BIC/SWIFT, or a USDT/USDC wallet on ERC-20."],
  ["Why do I need a one-time code to change my payout details?", "It stops anyone who gains access to a device from redirecting your money. The code is sent to your registered email and is required for every change."],
  ["What is the safety hold?", `New sellers can have their earliest payouts held up to ${MP_SAFETY_HOLD_DAYS} days while their order history builds. It protects buyers and keeps the platform clean of fraud.`],
  ["Can I withdraw Top-up or Referral Heat?", "No. Top-up credit pays for platform services, and Referral Reward Heat is internal credit for boosts and Marketplace orders. Only the Earn wallet holds withdrawable money."],
  ["What happens in a dispute?", "The held funds stay with the platform until our team reviews the order chat, files and deadlines. If the work was delivered as described, the money is released to you."],
  ["Do I get invoices and statements?", "Yes. Every paid order produces a sequential PDF invoice emailed to you and stored in your account, and Transactions exports your full history."],
  ["Is my income taxed by the platform?", "No. We invoice in AED and provide the documents; declaring and paying tax in your country is your responsibility."],
  ["Can a company earn here?", "Yes. Complete business verification with company name and trade licence, and payouts go to the company account."],
  ["What if a buyer disappears after delivery?", "Orders auto-accept after the review window, so an unresponsive buyer cannot block your payment indefinitely."],
];

const STEP_KEYS = ["step1", "step2", "step3", "step4", "step5"] as const;
const WALLET_KEYS = ["wallet1", "wallet2", "wallet3"] as const;
const RULE_KEYS = ["rule1", "rule2", "rule3", "rule4", "rule5", "rule6", "rule7"] as const;
const TRUST_KEYS = [
  { key: "trust1", icon: ShieldCheck },
  { key: "trust2", icon: Lock },
  { key: "trust3", icon: Timer },
  { key: "trust4", icon: Banknote },
];
const FAQ_KEYS = FAQ_EN.map((_, i) => `faq${i + 1}`);

export function EarnHowItWorks() {
  const { t } = useT();
  const vars = { EARN_FEE_PERCENT, MP_EARNINGS_PENDING_DAYS, MP_SAFETY_HOLD_DAYS };

  const STEPS = STEP_KEYS.map((k) => ({
    key: k,
    title: t(`hiw.earn.${k}.title`),
    body: t(`hiw.earn.${k}.body`, vars),
  }));
  const WALLETS = WALLET_KEYS.map((k) => [t(`hiw.earn.${k}.term`), t(`hiw.earn.${k}.def`)] as const);
  const RULES = RULE_KEYS.map((k) => t(`hiw.earn.${k}`, vars));
  const TRUST = TRUST_KEYS.map((p) => ({
    ...p,
    title: t(`hiw.earn.${p.key}.title`),
    body: t(`hiw.earn.${p.key}.body`, vars),
  }));
  const FAQ: [string, string][] = FAQ_KEYS.map((k) => [t(`hiw.earn.${k}.q`), t(`hiw.earn.${k}.a`, vars)]);

  return (
    <div>
      <p className="mt-8 max-w-2xl text-muted-foreground">{t("hiw.earn.intro")}</p>

      <section className="mt-12" aria-labelledby="earn-steps-heading">
        <div className="flex items-center gap-2">
          <Coins className="size-5 text-primary" aria-hidden="true" />
          <h2 id="earn-steps-heading" className="font-display text-2xl font-bold">
            {t("hiw.earn.stepsHeading")}
          </h2>
        </div>
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <ol className="contents list-none">
            {STEPS.map((s) => (
              <li key={s.key} className="card-surface p-5">
                <h3 className="font-display text-lg font-bold">{s.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{s.body}</p>
              </li>
            ))}
          </ol>
          <div className="card-surface flex flex-col justify-center p-5">
            <h3 className="font-display text-lg font-bold">{t("hiw.earn.startTitle")}</h3>
            <p className="mt-1 text-sm text-muted-foreground">{t("hiw.earn.startBody")}</p>
            <div className="mt-4 flex flex-col gap-3 sm:flex-row">
              <Button asChild variant="hot" className="w-full sm:w-auto">
                <Link to="/marketplace/sell">{t("hiw.earn.becomeSeller")}</Link>
              </Button>
              <Button asChild variant="outline" className="w-full sm:w-auto">
                <Link to="/app/wallets">{t("hiw.earn.myWallets")}</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="mt-14" aria-labelledby="earn-wallets-heading">
        <h2 id="earn-wallets-heading" className="font-display text-2xl font-bold">
          {t("hiw.earn.walletsHeading")}
        </h2>
        <dl className="mt-5 grid gap-3 sm:grid-cols-3">
          {WALLETS.map(([term, def]) => (
            <div key={term} className="card-surface p-4">
              <dt className="font-display font-bold">{term}</dt>
              <dd className="mt-1 text-sm text-muted-foreground">{def}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="mt-14" aria-labelledby="earn-numbers-heading">
        <h2 id="earn-numbers-heading" className="font-display text-2xl font-bold">
          {t("hiw.earn.numbersHeading")}
        </h2>
        <div className="card-surface mt-5 grid gap-4 p-5 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{t("hiw.earn.feeLabel")}</p>
            <p className="mt-1 font-display text-2xl font-bold text-primary">{EARN_FEE_PERCENT}%</p>
            <p className="text-xs text-muted-foreground">{t("hiw.earn.feeDesc")}</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{t("hiw.earn.clearanceLabel")}</p>
            <p className="mt-1 font-display text-2xl font-bold">{MP_EARNINGS_PENDING_DAYS} {t("hiw.earn.daysWord")}</p>
            <p className="text-xs text-muted-foreground">{t("hiw.earn.clearanceDesc")}</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{t("hiw.earn.minPayoutLabel")}</p>
            <p className="mt-1 font-display text-2xl font-bold">US$100</p>
            <p className="text-xs text-muted-foreground">{t("hiw.earn.minPayoutDesc")}</p>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">{t("hiw.earn.payoutTimeLabel")}</p>
            <p className="mt-1 font-display text-2xl font-bold">48h + 5–8d</p>
            <p className="text-xs text-muted-foreground">{t("hiw.earn.payoutTimeDesc")}</p>
          </div>
        </div>
      </section>

      <section className="mt-14" aria-labelledby="earn-rules-heading">
        <h2 id="earn-rules-heading" className="font-display text-2xl font-bold">
          {t("hiw.earn.rulesHeading")}
        </h2>
        <div className="card-surface mt-5 p-6">
          <ul className="list-disc space-y-2 pl-5 text-sm text-muted-foreground">
            {RULES.map((r) => (
              <li key={r}>{r}</li>
            ))}
          </ul>
        </div>
      </section>

      <section className="mt-14" aria-labelledby="earn-trust-heading">
        <h2 id="earn-trust-heading" className="font-display text-2xl font-bold">
          {t("hiw.earn.trustHeading")}
        </h2>
        <ul className="mt-5 grid list-none gap-4 sm:grid-cols-2">
          {TRUST.map((p) => (
            <li key={p.key} className="card-surface p-5">
              <p.icon className="size-5 text-primary" aria-hidden="true" />
              <h3 className="mt-3 font-display text-lg font-bold">{p.title}</h3>
              <p className="mt-1.5 text-sm text-muted-foreground">{p.body}</p>
            </li>
          ))}
        </ul>
      </section>

      <section className="mt-14" aria-labelledby="earn-faq-heading">
        <div className="flex flex-wrap items-center gap-2">
          <ListChecks className="size-5 text-primary" aria-hidden="true" />
          <h2 id="earn-faq-heading" className="font-display text-2xl font-bold">
            {t("hiw.earn.faqHeading")}
          </h2>
          <Badge variant="outline">{t("hiw.earn.faqCount", { count: FAQ.length })}</Badge>
        </div>
        <div className="mt-5 divide-y divide-border rounded-xl border border-border">
          {FAQ.map(([q, a], i) => (
            <details key={q} open={i < 3} className="group px-5 py-4 [&_summary::-webkit-details-marker]:hidden">
              <summary className="cursor-pointer list-none font-display font-bold">{q}</summary>
              <p className="mt-2 text-sm text-muted-foreground">{a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="mt-14" aria-labelledby="earn-contact-heading">
        <div className="flex items-center gap-2">
          <LifeBuoy className="size-5 text-primary" aria-hidden="true" />
          <h2 id="earn-contact-heading" className="font-display text-2xl font-bold">
            {t("hiw.earn.contactHeading")}
          </h2>
        </div>
        <div className="mt-5">
          <ContactForm defaultTopic="Earn (creator payouts)" />
        </div>
      </section>
    </div>
  );
}

export const EARN_FAQ = FAQ_EN;
